import React, { useEffect } from 'react';
import { MainLayout } from '../layouts';
import { useSiteName, useContactEmail } from '../contexts/SiteConfigContext';
import analyticsService from '../services/analytics';
import { usePageDocumentTitle } from '../hooks/useDocumentTitle';

const PrivacyPage: React.FC = () => {
  usePageDocumentTitle('Privacy Policy');
  const siteName = useSiteName();
  const contactEmail = useContactEmail();

  // Track page view when component mounts
  useEffect(() => {
    try {
      analyticsService.trackPageView('privacy', undefined, new Date().toISOString().split('T')[0], {
        referrer: document.referrer || 'direct'
      });
      console.log('📊 PrivacyPage: Page view tracked');
    } catch (analyticsError) {
      console.warn('Analytics tracking failed:', analyticsError);
    }
  }, []);

  return (
    <MainLayout>
      <div className="bg-white p-6 rounded-lg shadow-card">
        <h1 className="text-2xl font-bold mb-6">Privacy Policy</h1>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">1. Introduction</h2>
          <p className="mb-3">
            At {siteName}, we respect your privacy and are committed to protecting your personal data.
            This privacy policy explains how we collect, use, and safeguard your information when you use our website.
          </p>
          <p className="mb-3">
            We do not share your personal information with third parties for marketing purposes.
            Your data is collected and used only for the purposes described in this policy.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">2. Information We Collect</h2>
          <p className="mb-3">
            We collect the following types of information:
          </p>
          <ul className="list-disc pl-6 mb-3">
            <li><strong>Account Information:</strong> When you register, we collect your name, email address, and password.</li>
            <li><strong>Phone Number:</strong> We collect your mobile number for account verification via SMS.</li>
            <li><strong>Usage Data:</strong> Information about how you use our website, including your betting preferences and tips viewed.</li>
            <li><strong>Technical Data:</strong> IP address, browser type, device information, and cookies.</li>
            <li><strong>Contact Information:</strong> When you contact us through our contact form, we collect your name, email, and message content.</li>
          </ul>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">3. SMS Communications</h2>
          <p className="mb-3">
            When you register or request a password reset, {siteName} sends one-time verification
            codes by SMS via Brevo. These messages are transactional and required to use the service.
          </p>
          <ul className="list-disc pl-6 mb-3">
            <li><strong>Message types:</strong> Account verification codes and password reset codes.</li>
            <li><strong>Frequency:</strong> Messages are only sent when you initiate a verification action.</li>
            <li><strong>Charges:</strong> Message and data rates may apply depending on your carrier.</li>
            <li><strong>Opt-out:</strong> Reply STOP to any SMS to opt out. You may also email {contactEmail}.</li>
            <li><strong>Help:</strong> Reply HELP to any SMS, or contact {contactEmail}.</li>
            <li><strong>Data sharing:</strong> Your phone number is used solely for authentication and is not shared with third parties for marketing.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">4. How We Use Your Information</h2>
          <p className="mb-3">
            We use your information for the following purposes:
          </p>
          <ul className="list-disc pl-6 mb-3">
            <li>To provide and maintain our service</li>
            <li>To notify you about changes to our service</li>
            <li>To allow you to participate in interactive features</li>
            <li>To provide customer support</li>
            <li>To gather analysis or valuable information to improve our service</li>
            <li>To monitor the usage of our service</li>
            <li>To detect, prevent, and address technical issues</li>
          </ul>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">5. Data Security</h2>
          <p className="mb-3">
            We implement appropriate security measures to protect your personal data against unauthorized access,
            alteration, disclosure, or destruction. Your password is stored in an encrypted format, and sensitive data
            is protected with encryption during transmission.
          </p>
          <p className="mb-3">
            However, no method of transmission over the Internet or electronic storage is 100% secure.
            While we strive to use commercially acceptable means to protect your personal data,
            we cannot guarantee its absolute security.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">6. Cookies and Tracking</h2>
          <p className="mb-3">
            We use cookies and similar tracking technologies to track activity on our website and hold certain information.
            Cookies are files with a small amount of data that may include an anonymous unique identifier.
          </p>
          <p className="mb-3">
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            However, if you do not accept cookies, you may not be able to use some portions of our service.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">7. Third-Party Services</h2>
          <p className="mb-3">
            We may employ third-party companies and individuals to facilitate our service, provide the service on our behalf,
            perform service-related services, or assist us in analyzing how our service is used.
          </p>
          <p className="mb-3">
            These third parties have access to your personal data only to perform these tasks on our behalf
            and are obligated not to disclose or use it for any other purpose.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">8. Your Data Protection Rights</h2>
          <p className="mb-3">
            You have the following data protection rights:
          </p>
          <ul className="list-disc pl-6 mb-3">
            <li>The right to access, update, or delete the information we have on you</li>
            <li>The right of rectification - the right to have your information corrected if it is inaccurate or incomplete</li>
            <li>The right to object to our processing of your personal data</li>
            <li>The right of restriction - the right to request that we restrict the processing of your personal information</li>
            <li>The right to data portability - the right to receive a copy of your personal data in a structured, machine-readable format</li>
            <li>The right to withdraw consent at any time where we relied on your consent to process your personal information</li>
          </ul>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">9. Changes to This Privacy Policy</h2>
          <p className="mb-3">
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
          </p>
          <p className="mb-3">
            You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
          </p>
        </section>
        
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3">10. Contact Us</h2>
          <p className="mb-3">
            If you have any questions about this Privacy Policy, please contact us at {contactEmail}.
          </p>
        </section>
        
        <div className="text-sm text-gray-500 mt-8">
          Last updated: May 5, 2025
        </div>
      </div>
    </MainLayout>
  );
};

export default PrivacyPage;
